import React, { useEffect } from 'react';
import { LogOut, X, AlertTriangle } from 'lucide-react';
import { tg } from '../telegram';
import { AuthUser } from '../types';

interface LogoutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  user: AuthUser | null;
}

export const LogoutConfirmModal: React.FC<LogoutConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isStaff = user?.role === 'STAFF';
  const displayName = user?.name || user?.username || 'Người dùng';

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        tg.haptic.impact('light');
        onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-modal-title"
    >
      <div
        className="modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 420,
          padding: '24px 20px',
          gap: 16,
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {/* Header with Close Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -10 }}>
          <button
            onClick={() => {
              tg.haptic.impact('light');
              onClose();
            }}
            className="stepper-btn"
            style={{ width: 32, height: 32 }}
            aria-label="Đóng"
          >
            <X size={16} />
          </button>
        </div>

        {/* Warning Icon Badge */}
        <div style={{ textAlign: 'center', marginTop: -6 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: 'var(--color-danger-bg)',
              color: 'var(--color-danger)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            <LogOut size={26} strokeWidth={2.2} />
          </div>

          <h3
            id="logout-modal-title"
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: 'var(--text-primary)',
              margin: '0 0 6px 0',
            }}
          >
            Xác Nhận Đăng Xuất
          </h3>

          <p
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Bạn có chắc chắn muốn đăng xuất tài khoản khỏi thiết bị này?
          </p>
        </div>

        {/* User Card */}
        {user && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              backgroundColor: 'var(--bg-surface-subtle)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-color)',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                backgroundColor: 'var(--bg-active)',
                color: 'var(--primary-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: 'var(--text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                @{user.username}
              </div>
            </div>

            <span
              className={`badge ${isStaff ? 'badge-success' : 'badge-warning'}`}
              style={{ fontSize: 11, padding: '4px 8px', flexShrink: 0 }}
            >
              {isStaff ? 'Staff' : 'Admin'}
            </span>
          </div>
        )}

        {/* Informational notice */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            backgroundColor: 'var(--color-warning-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            color: 'var(--color-warning)',
            lineHeight: 1.4,
          }}
        >
          <AlertTriangle size={15} style={{ flexShrink: 0 }} />
          <span>Phiên làm việc sẽ kết thúc. Bạn cần mật khẩu để đăng nhập lại.</span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <button
            onClick={() => {
              tg.haptic.warning();
              onConfirm();
            }}
            className="btn btn-danger btn-full"
            style={{ fontSize: 15, fontWeight: 700, minHeight: 46 }}
          >
            <LogOut size={16} /> Đăng Xuất
          </button>

          <button
            onClick={() => {
              tg.haptic.impact('light');
              onClose();
            }}
            className="btn btn-secondary btn-full"
            style={{ fontSize: 14, fontWeight: 600, minHeight: 44 }}
          >
            Ở Lại (Hủy Bỏ)
          </button>
        </div>
      </div>
    </div>
  );
};
